import { useState, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import pick from 'lodash/pick';
import ddt from '@wix/da-ddt';
import { useThrottle } from '../useThrottle';
import { useDeepEqualMemo } from '../useDeepEqualMemo';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { makeGetPrevLoadDims } from './redux/selectors';
import { updateMeasuredCookie } from './redux/actions';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
const logger = ddt.createLogger('measuredhook');
export const DEFAULT_SIZE = {
    desktop: {
        width: 1024,
        height: 768
    },
    mobile: {
        width: 375,
        height: 667
    }
};
const THROTTLE_TIMEOUT = 500;
const THROTTLE_OPTIONS = {
    trailing: true
};
export function useMeasureElementCookieState(cookieType, cookieDim = 'both', defaultSize = DEFAULT_SIZE, throttleOptions) {
    const dispatch = useDispatch();
    const isMobile = useContext(MobileContext);
    const getPrevLoadDims = useRef(makeGetPrevLoadDims());
    const appEnv = useSelector(getEnv);
    const clientHints = appEnv.clientHints;
    const prevLoadDims = useSelector((state)=>cookieType !== undefined ? getPrevLoadDims.current(state, cookieType, cookieDim) : {
            width: 0,
            height: 0
        }, ()=>true // we only want to select this value once, as after mount we rely on actual measurements
    );
    // When we are using the standard defaults we usually mean the viewport, in that case we switch
    // the defaults to use the client hints when available giving us more accurate screen size
    // for android we dont use its default from client hint because android reports wrong size on pageloads
    const defaultSizeForPlatform = useDeepEqualMemo((()=>{
        if (defaultSize === DEFAULT_SIZE && clientHints) {
            return {
                width: isMobile ? DEFAULT_SIZE.mobile.width : clientHints.width,
                height: clientHints.height
            };
        } else {
            return isMobile ? defaultSize.mobile : defaultSize.desktop;
        }
    })());
    const [containerSize, setContainerSize] = useState(()=>prevLoadDims && (prevLoadDims.width > 0 || prevLoadDims.height > 0) ? prevLoadDims : defaultSizeForPlatform);
    var _throttleOptions_throttle, _throttleOptions_throttleOptions;
    const updateContainerSize = useThrottle((newSize)=>{
        // requirement for updating the size: have the interesting measurement exist
        // and be different from what we already have
        const widthChanged = [
            'width',
            'both'
        ].includes(cookieDim) && !!newSize.width && (!containerSize || newSize.width !== containerSize.width);
        const heightChanged = [
            'height',
            'both'
        ].includes(cookieDim) && !!newSize.height && (!containerSize || newSize.height !== containerSize.height);
        if (widthChanged || heightChanged) {
            const sanitizedSize = pick(newSize, 'width', 'height');
            logger.log('Updated measurements', sanitizedSize);
            setContainerSize(sanitizedSize);
            cookieType !== undefined && dispatch(updateMeasuredCookie(cookieType, cookieDim, sanitizedSize));
        }
    }, [
        containerSize,
        cookieDim,
        cookieType,
        dispatch
    ], (_throttleOptions_throttle = throttleOptions === null || throttleOptions === void 0 ? void 0 : throttleOptions.throttle) !== null && _throttleOptions_throttle !== void 0 ? _throttleOptions_throttle : THROTTLE_TIMEOUT, (_throttleOptions_throttleOptions = throttleOptions === null || throttleOptions === void 0 ? void 0 : throttleOptions.throttleOptions) !== null && _throttleOptions_throttleOptions !== void 0 ? _throttleOptions_throttleOptions : THROTTLE_OPTIONS);
    return {
        containerSize,
        updateContainerSize,
        defaultSizeForPlatform
    };
}
